import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, AlertCircle, CheckCircle2, XCircle, Loader2, Download, Database, Clock, FileVideo, Play, Search, ListVideo, Link2, PenTool } from "lucide-react";
import Papa from "papaparse";
import { useToast } from "@/hooks/use-toast";

interface OrphanedResponse {
  test_response_id: string;
  test_id: string;
  test_title: string;
  candidate_email: string;
  candidate_name: string;
  completed_at: string;
  expected_questions: number;
  saved_responses: number;
  missing: number;
}

interface QuestionTimeline {
  test_response_id: string;
  question_id: string;
  question_order: number;
  video_url: string;
  is_missing: boolean;
  timeline_start: string | null;
  timeline_end: string | null;
  has_monitoring_data: boolean;
}

interface VideoQuestionStatus {
  test_response_id: string;
  question_id: string;
  question_order: number;
  video_url: string;
  is_missing: boolean;
}

interface RecoveryResult {
  test_response_id: string;
  question_id: string;
  status: 'recovered' | 'not_found' | 'already_exists' | 'error';
  video_url?: string;
  audio_url?: string;
  error_message?: string;
  question_response_id?: string;
}

// Step 5 interfaces
interface StorageVideoAnalysis {
  test_response_id: string;
  candidate_email: string;
  candidate_name: string;
  test_title: string;
  total_videos_in_storage: number;
  total_videos_inserted: number;
  uninserted_video_count: number;
}

interface VideoInventoryItem {
  test_response_id: string;
  file_name: string;
  file_path: string;
  uploaded_at: string;
  is_inserted: boolean;
  inserted_question_id: string | null;
}

interface ProposedMatch {
  test_response_id: string;
  question_id: string;
  question_order: number;
  video_path: string;
  video_uploaded_at: string;
  audio_path: string | null;
  confidence: 'high' | 'medium' | 'low';
  timeline_start: string;
  timeline_end: string;
  match_reason: string;
  is_already_inserted: boolean;
}

interface UnmatchedQuestion {
  test_response_id: string;
  question_id: string;
  question_order: number;
  timeline_start: string;
  timeline_end: string;
  window_start: string;
  window_end: string;
  videos_in_response: number;
  nearest_video_time: string | null;
  nearest_video_distance_seconds: number | null;
  reason: string;
}

interface UnmatchedVideo {
  test_response_id: string;
  file_name: string;
  file_path: string;
  uploaded_at: string;
  questions_in_response: number;
  nearest_question_end: string | null;
  nearest_question_distance_seconds: number | null;
  reason: string;
}

interface InsertResult {
  test_response_id: string;
  question_id: string;
  status: 'inserted' | 'already_exists' | 'error';
  question_response_id?: string;
  error_message?: string;
}

export default function ResponseRecovery() {
  const { toast } = useToast();

  // Step 1 state
  const [startDate, setStartDate] = useState<string>('2025-11-27');
  const [endDate, setEndDate] = useState<string>('2025-11-30');
  const [step1Loading, setStep1Loading] = useState(false);
  const [orphanedResponses, setOrphanedResponses] = useState<OrphanedResponse[]>([]);

  // Step 2 state
  const [step2File, setStep2File] = useState<File | null>(null);
  const [step2Loading, setStep2Loading] = useState(false);
  const [step2Progress, setStep2Progress] = useState<{ current: number; total: number } | null>(null);
  const [questionTimelines, setQuestionTimelines] = useState<QuestionTimeline[]>([]);

  // Step 3 state
  const [step3File, setStep3File] = useState<File | null>(null);
  const [step3Loading, setStep3Loading] = useState(false);
  const [step3Progress, setStep3Progress] = useState<{ current: number; total: number } | null>(null);
  const [recoveryResults, setRecoveryResults] = useState<RecoveryResult[]>([]);

  // Step 4 state
  const [step4File, setStep4File] = useState<File | null>(null);
  const [step4Loading, setStep4Loading] = useState(false);
  const [evaluationTriggered, setEvaluationTriggered] = useState<number>(0);

  // New Step: List Video Question Status state
  const [stepListVideoFile, setStepListVideoFile] = useState<File | null>(null);
  const [stepListVideoLoading, setStepListVideoLoading] = useState(false);
  const [stepListVideoProgress, setStepListVideoProgress] = useState<{ current: number; total: number } | null>(null);
  const [videoQuestionStatus, setVideoQuestionStatus] = useState<VideoQuestionStatus[]>([]);

  // Step 5 state
  const [step5File, setStep5File] = useState<File | null>(null);
  const [step5Loading, setStep5Loading] = useState(false);
  const [step5Progress, setStep5Progress] = useState<{ current: number; total: number } | null>(null);
  const [storageAnalysis, setStorageAnalysis] = useState<StorageVideoAnalysis[]>([]);

  // Step 5a state
  const [step5aFile, setStep5aFile] = useState<File | null>(null);
  const [step5aLoading, setStep5aLoading] = useState(false);
  const [step5aProgress, setStep5aProgress] = useState<{ current: number; total: number } | null>(null);
  const [videoInventory, setVideoInventory] = useState<VideoInventoryItem[]>([]);

  // Step 5b state
  const [step5bFile, setStep5bFile] = useState<File | null>(null);
  const [step5bLoading, setStep5bLoading] = useState(false);
  const [step5bProgress, setStep5bProgress] = useState<{ current: number; total: number } | null>(null);
  const [missingTimelines, setMissingTimelines] = useState<QuestionTimeline[]>([]);

  // Step 5c state
  const [step5cVideoFile, setStep5cVideoFile] = useState<File | null>(null);
  const [step5cTimelineFile, setStep5cTimelineFile] = useState<File | null>(null);
  const [step5cLoading, setStep5cLoading] = useState(false);
  const [proposedMatches, setProposedMatches] = useState<ProposedMatch[]>([]);
  const [unmatchedQuestions, setUnmatchedQuestions] = useState<UnmatchedQuestion[]>([]);
  const [unmatchedVideos, setUnmatchedVideos] = useState<UnmatchedVideo[]>([]);
  const [step5cSummary, setStep5cSummary] = useState<any>(null);

  // Step 5d state
  const [step5dFile, setStep5dFile] = useState<File | null>(null);
  const [step5dLoading, setStep5dLoading] = useState(false);
  const [insertResults, setInsertResults] = useState<InsertResult[]>([]);

  // Helper to fetch all rows with pagination
  const fetchAllRows = async <T,>(
    queryFn: (offset: number, limit: number) => Promise<{ data: T[] | null; error: any }>
  ): Promise<T[]> => {
    const allData: T[] = [];
    const batchSize = 1000;
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
      const { data, error } = await queryFn(offset, batchSize);
      if (error) throw error;
      if (data && data.length > 0) {
        allData.push(...data);
        offset += batchSize;
        hasMore = data.length === batchSize;
      } else {
        hasMore = false;
      }
    }
    return allData;
  };

  // Step 1: Find orphaned responses in date range (uses edge function to bypass RLS)
  const runStep1 = async () => {
    setStep1Loading(true);
    setOrphanedResponses([]);

    try {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      console.log('[Recovery Step 1] Calling edge function to identify orphaned responses...');
      
      const { data: result, error } = await supabase.functions.invoke('identify-orphaned-responses', {
        body: { 
          startDate: start.toISOString(), 
          endDate: end.toISOString() 
        }
      });

      if (error) throw error;

      const orphanedData = result?.orphanedResponses || [];
      console.log('[Recovery Step 1] Found', orphanedData.length, 'orphaned responses');

      // Map to expected interface
      const orphaned: OrphanedResponse[] = orphanedData.map((r: any) => ({
        test_response_id: r.test_response_id,
        test_id: r.test_id,
        test_title: r.test_title || 'Unknown',
        candidate_email: r.candidate_email || 'Unknown',
        candidate_name: r.candidate_name || 'Unknown',
        completed_at: r.completed_at,
        expected_questions: r.expected_questions,
        saved_responses: r.saved_responses,
        missing: r.missing_responses
      }));

      setOrphanedResponses(orphaned);
      toast({
        title: "Step 1 Complete",
        description: `Found ${orphaned.length} responses with missing VIDEO data (${orphaned.filter(o => o.saved_responses === 0).length} fully orphaned)`
      });
    } catch (error: any) {
      console.error('[Recovery Step 1] Error:', error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setStep1Loading(false);
    }
  };

  const downloadStep1CSV = () => {
    const csv = Papa.unparse(orphanedResponses);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orphaned-responses-${startDate}-${endDate}.csv`;
    a.click();
  };

  // Step 2: Generate question timelines
  const handleStep2FileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setStep2File(e.target.files[0]);
  };

  const runStep2 = async () => {
    if (!step2File) return;
    setStep2Loading(true);
    setQuestionTimelines([]);

    try {
      const text = await step2File.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
      const data = parsed.data as OrphanedResponse[];
      const testResponseIds = data.map(d => d.test_response_id).filter(Boolean);

      console.log('[Recovery Step 2] Processing', testResponseIds.length, 'test responses');

      const allTimelines: QuestionTimeline[] = [];
      const BATCH_SIZE = 20;

      for (let i = 0; i < testResponseIds.length; i += BATCH_SIZE) {
        const batch = testResponseIds.slice(i, i + BATCH_SIZE);
        setStep2Progress({ current: i, total: testResponseIds.length });

        const { data: result, error } = await supabase.functions.invoke('get-question-timelines', {
          body: { test_response_ids: batch }
        });

        if (error) {
          console.error('[Recovery Step 2] Error:', error);
        } else if (result?.results) {
          allTimelines.push(...result.results);
        }
      }

      setQuestionTimelines(allTimelines);
      setStep2Progress({ current: testResponseIds.length, total: testResponseIds.length });
      toast({
        title: "Step 2 Complete",
        description: `Generated ${allTimelines.length} timeline entries (${allTimelines.filter(t => t.is_missing).length} missing)`
      });
    } catch (error: any) {
      console.error('[Recovery Step 2] Error:', error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setStep2Loading(false);
      setStep2Progress(null);
    }
  };

  const downloadStep2CSV = () => {
    const csv = Papa.unparse(questionTimelines);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `question-timelines-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Step 3: Match videos and recover
  const handleStep3FileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setStep3File(e.target.files[0]);
  };

  const runStep3 = async () => {
    if (!step3File) return;
    setStep3Loading(true);
    setRecoveryResults([]);

    try {
      const text = await step3File.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
      const data = parsed.data as QuestionTimeline[];
      
      // Filter only missing questions with timeline data
      const toRecover = data.filter(d => 
        d.is_missing && 
        d.timeline_start && 
        d.timeline_end &&
        d.has_monitoring_data
      );

      console.log('[Recovery Step 3] Processing', toRecover.length, 'missing questions');

      const allResults: RecoveryResult[] = [];
      const BATCH_SIZE = 10;

      for (let i = 0; i < toRecover.length; i += BATCH_SIZE) {
        const batch = toRecover.slice(i, i + BATCH_SIZE);
        setStep3Progress({ current: i, total: toRecover.length });

        const { data: result, error } = await supabase.functions.invoke('recover-orphaned-responses', {
          body: { 
            items: batch.map(b => ({
              test_response_id: b.test_response_id,
              question_id: b.question_id,
              timeline_start: b.timeline_start,
              timeline_end: b.timeline_end
            }))
          }
        });

        if (error) {
          console.error('[Recovery Step 3] Error:', error);
        } else if (result?.results) {
          allResults.push(...result.results);
        }
      }

      setRecoveryResults(allResults);
      setStep3Progress({ current: toRecover.length, total: toRecover.length });

      const recovered = allResults.filter(r => r.status === 'recovered').length;
      toast({
        title: "Step 3 Complete",
        description: `Recovered ${recovered} of ${toRecover.length} question responses`
      });
    } catch (error: any) {
      console.error('[Recovery Step 3] Error:', error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setStep3Loading(false);
      setStep3Progress(null);
    }
  };

  const downloadStep3CSV = () => {
    const csv = Papa.unparse(recoveryResults);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recovery-results-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Step 4: Trigger evaluation
  const handleStep4FileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setStep4File(e.target.files[0]);
  };

  const runStep4 = async () => {
    if (!step4File) return;
    setStep4Loading(true);
    setEvaluationTriggered(0);

    try {
      const text = await step4File.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
      const data = parsed.data as RecoveryResult[];
      
      // Filter only successfully recovered responses
      const toEvaluate = data
        .filter(d => d.status === 'recovered' && d.question_response_id)
        .map(d => d.question_response_id!);

      console.log('[Recovery Step 4] Triggering evaluation for', toEvaluate.length, 'responses');

      const { data: result, error } = await supabase.functions.invoke('trigger-batch-evaluation', {
        body: { question_response_ids: toEvaluate }
      });

      if (error) throw error;

      setEvaluationTriggered(result?.updated || 0);
      toast({
        title: "Step 4 Complete",
        description: `Triggered evaluation for ${result?.updated || 0} question responses`
      });
    } catch (error: any) {
      console.error('[Recovery Step 4] Error:', error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setStep4Loading(false);
    }
  };

  // Step 5: Analyze storage videos
  const handleStep5FileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setStep5File(e.target.files[0]);
  };

  const runStep5 = async () => {
    if (!step5File) return;
    setStep5Loading(true);
    setStorageAnalysis([]);

    try {
      const text = await step5File.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
      const data = parsed.data as OrphanedResponse[];
      const testResponseIds = data.map(d => d.test_response_id).filter(Boolean);

      console.log('[Recovery Step 5] Processing', testResponseIds.length, 'test responses');

      const allAnalysis: StorageVideoAnalysis[] = [];
      const BATCH_SIZE = 10;

      for (let i = 0; i < testResponseIds.length; i += BATCH_SIZE) {
        const batch = testResponseIds.slice(i, i + BATCH_SIZE);
        setStep5Progress({ current: i, total: testResponseIds.length });

        const { data: result, error } = await supabase.functions.invoke('analyze-storage-videos', {
          body: { test_response_ids: batch }
        });

        if (error) {
          console.error('[Recovery Step 5] Error:', error);
        } else if (result?.results) {
          allAnalysis.push(...result.results);
        }
      }

      setStorageAnalysis(allAnalysis);
      setStep5Progress({ current: testResponseIds.length, total: testResponseIds.length });

      const withUninserted = allAnalysis.filter(a => a.uninserted_video_count > 0);
      toast({
        title: "Step 5 Complete",
        description: `Found ${withUninserted.length} responses with uninserted videos`
      });
    } catch (error: any) {
      console.error('[Recovery Step 5] Error:', error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setStep5Loading(false);
      setStep5Progress(null);
    }
  };

  const downloadStep5CSV = () => {
    const csv = Papa.unparse(storageAnalysis);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `storage-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // New Step: List Video Question Status
  const handleStepListVideoFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setStepListVideoFile(e.target.files[0]);
  };

  const runStepListVideo = async () => {
    if (!stepListVideoFile) return;
    setStepListVideoLoading(true);
    setVideoQuestionStatus([]);

    try {
      const text = await stepListVideoFile.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
      const data = parsed.data as OrphanedResponse[];
      const testResponseIds = data.map(d => d.test_response_id).filter(Boolean);

      console.log('[Recovery List Video] Processing', testResponseIds.length, 'test responses');

      const allStatus: VideoQuestionStatus[] = [];
      const BATCH_SIZE = 20;

      for (let i = 0; i < testResponseIds.length; i += BATCH_SIZE) {
        const batch = testResponseIds.slice(i, i + BATCH_SIZE);
        setStepListVideoProgress({ current: i, total: testResponseIds.length });

        const { data: result, error } = await supabase.functions.invoke('list-video-question-status', {
          body: { test_response_ids: batch }
        });

        if (error) {
          console.error('[Recovery List Video] Error:', error);
        } else if (result?.results) {
          allStatus.push(...result.results);
        }
      }

      setVideoQuestionStatus(allStatus);
      setStepListVideoProgress({ current: testResponseIds.length, total: testResponseIds.length });
      
      const missingCount = allStatus.filter(s => s.is_missing).length;
      const savedCount = allStatus.filter(s => !s.is_missing).length;
      toast({
        title: "List Video Questions Complete",
        description: `Found ${allStatus.length} video questions: ${savedCount} saved, ${missingCount} missing`
      });
    } catch (error: any) {
      console.error('[Recovery List Video] Error:', error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setStepListVideoLoading(false);
      setStepListVideoProgress(null);
    }
  };

  const downloadStepListVideoCSV = () => {
    const csv = Papa.unparse(videoQuestionStatus);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `video-question-status-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Step 5a: Get video inventory
  const handleStep5aFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setStep5aFile(e.target.files[0]);
  };

  const runStep5a = async () => {
    if (!step5aFile) return;
    setStep5aLoading(true);
    setVideoInventory([]);

    try {
      const text = await step5aFile.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
      const data = parsed.data as StorageVideoAnalysis[];
      
      // Filter only those with uninserted videos
      const toProcess = data.filter(d => d.uninserted_video_count > 0);
      const testResponseIds = toProcess.map(d => d.test_response_id).filter(Boolean);

      console.log('[Recovery Step 5a] Processing', testResponseIds.length, 'test responses with uninserted videos');

      const allInventory: VideoInventoryItem[] = [];
      const BATCH_SIZE = 10;

      for (let i = 0; i < testResponseIds.length; i += BATCH_SIZE) {
        const batch = testResponseIds.slice(i, i + BATCH_SIZE);
        setStep5aProgress({ current: i, total: testResponseIds.length });

        const { data: result, error } = await supabase.functions.invoke('get-video-inventory', {
          body: { test_response_ids: batch }
        });

        if (error) {
          console.error('[Recovery Step 5a] Error:', error);
        } else if (result?.results) {
          allInventory.push(...result.results);
        }
      }

      setVideoInventory(allInventory);
      setStep5aProgress({ current: testResponseIds.length, total: testResponseIds.length });

      const uninserted = allInventory.filter(v => !v.is_inserted);
      toast({
        title: "Step 5a Complete",
        description: `Found ${allInventory.length} videos (${uninserted.length} uninserted)`
      });
    } catch (error: any) {
      console.error('[Recovery Step 5a] Error:', error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setStep5aLoading(false);
      setStep5aProgress(null);
    }
  };

  const downloadStep5aCSV = () => {
    const csv = Papa.unparse(videoInventory);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `video-inventory-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Step 5b: Add Timelines to ALL Video Questions (accepts new CSV format)
  const handleStep5bFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setStep5bFile(e.target.files[0]);
  };

  const runStep5b = async () => {
    if (!step5bFile) return;
    setStep5bLoading(true);
    setMissingTimelines([]);

    try {
      const text = await step5bFile.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
      const data = parsed.data as VideoQuestionStatus[];

      console.log('[Recovery Step 5b] Processing', data.length, 'video question items');

      // Group items by test_response_id for batching
      const itemsByResponse = new Map<string, VideoQuestionStatus[]>();
      for (const item of data) {
        if (!item.test_response_id) continue;
        if (!itemsByResponse.has(item.test_response_id)) {
          itemsByResponse.set(item.test_response_id, []);
        }
        itemsByResponse.get(item.test_response_id)!.push(item);
      }

      const testResponseIds = [...itemsByResponse.keys()];
      const allTimelines: QuestionTimeline[] = [];
      const BATCH_SIZE = 20;

      for (let i = 0; i < testResponseIds.length; i += BATCH_SIZE) {
        const batchIds = testResponseIds.slice(i, i + BATCH_SIZE);
        setStep5bProgress({ current: i, total: testResponseIds.length });

        // Collect items for this batch
        const batchItems: VideoQuestionStatus[] = [];
        for (const id of batchIds) {
          batchItems.push(...(itemsByResponse.get(id) || []));
        }

        const { data: result, error } = await supabase.functions.invoke('get-question-timelines', {
          body: { items: batchItems }
        });

        if (error) {
          console.error('[Recovery Step 5b] Error:', error);
        } else if (result?.results) {
          // Keep ALL questions with timelines
          allTimelines.push(...result.results);
        }
      }

      setMissingTimelines(allTimelines);
      setStep5bProgress({ current: testResponseIds.length, total: testResponseIds.length });

      const missingWithMonitoring = allTimelines.filter(t => t.is_missing && t.has_monitoring_data).length;
      toast({
        title: "Step 5b Complete",
        description: `Generated timelines for ${allTimelines.length} questions (${allTimelines.filter(t => t.is_missing).length} missing, ${missingWithMonitoring} recoverable)`
      });
    } catch (error: any) {
      console.error('[Recovery Step 5b] Error:', error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setStep5bLoading(false);
      setStep5bProgress(null);
    }
  };

  const downloadStep5bCSV = () => {
    const csv = Papa.unparse(missingTimelines);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `missing-timelines-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Step 5c: Match uninserted videos to missing questions
  const handleStep5cVideoFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setStep5cVideoFile(e.target.files[0]);
  };

  const handleStep5cTimelineFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setStep5cTimelineFile(e.target.files[0]);
  };

  const runStep5c = async () => {
    if (!step5cVideoFile || !step5cTimelineFile) return;
    setStep5cLoading(true);
    setProposedMatches([]);
    setUnmatchedQuestions([]);
    setUnmatchedVideos([]);
    setStep5cSummary(null);

    try {
      // Parse video inventory CSV
      const videoText = await step5cVideoFile.text();
      const videoParsed = Papa.parse(videoText, { header: true, skipEmptyLines: true });
      const videoData = videoParsed.data as VideoInventoryItem[];
      
      // Filter to only uninserted videos
      const uninsertedVideos = videoData.filter(v => v.is_inserted === false || v.is_inserted.toString() === 'false');

      // Parse timeline CSV
      const timelineText = await step5cTimelineFile.text();
      const timelineParsed = Papa.parse(timelineText, { header: true, skipEmptyLines: true });
      const timelineData = timelineParsed.data as QuestionTimeline[];
      
      // Filter to only missing questions
      const missingQuestions = timelineData.filter(t => t.is_missing === true || t.is_missing.toString() === 'true');

      console.log('[Recovery Step 5c] Matching', uninsertedVideos.length, 'uninserted videos to', missingQuestions.length, 'missing questions');

      const { data: result, error } = await supabase.functions.invoke('match-uninserted-videos', {
        body: { 
          uninserted_videos: uninsertedVideos,
          missing_questions: missingQuestions
        }
      });

      if (error) throw error;

      setProposedMatches(result?.results || []);
      setUnmatchedQuestions(result?.unmatched_questions || []);
      setUnmatchedVideos(result?.unmatched_videos || []);
      setStep5cSummary(result?.summary || null);
      
      toast({
        title: "Step 5c Complete",
        description: `${result?.results?.length || 0} matches, ${result?.unmatched_questions?.length || 0} unmatched questions, ${result?.unmatched_videos?.length || 0} unmatched videos`
      });
    } catch (error: any) {
      console.error('[Recovery Step 5c] Error:', error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setStep5cLoading(false);
    }
  };

  const downloadStep5cMatchesCSV = () => {
    const csv = Papa.unparse(proposedMatches);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `proposed-matches-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const downloadStep5cUnmatchedQuestionsCSV = () => {
    const csv = Papa.unparse(unmatchedQuestions);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unmatched-questions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const downloadStep5cUnmatchedVideosCSV = () => {
    const csv = Papa.unparse(unmatchedVideos);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unmatched-videos-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Step 5d: Insert matched videos
  const handleStep5dFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setStep5dFile(e.target.files[0]);
  };

  const runStep5d = async () => {
    if (!step5dFile) return;
    setStep5dLoading(true);
    setInsertResults([]);

    try {
      const text = await step5dFile.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
      const data = parsed.data as ProposedMatch[];

      console.log('[Recovery Step 5d] Inserting', data.length, 'matched videos');

      const { data: result, error } = await supabase.functions.invoke('insert-matched-videos', {
        body: { matches: data }
      });

      if (error) throw error;

      setInsertResults(result?.results || []);

      const inserted = result?.summary?.inserted || 0;
      toast({
        title: "Step 5d Complete",
        description: `Inserted ${inserted} of ${data.length} matches`
      });
    } catch (error: any) {
      console.error('[Recovery Step 5d] Error:', error);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setStep5dLoading(false);
    }
  };

  const downloadStep5dCSV = () => {
    const csv = Papa.unparse(insertResults);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `insert-results-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Identify Orphaned Responses */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Step 1: Identify Orphaned Video Responses
          </CardTitle>
          <CardDescription>
            Find test responses with missing VIDEO question_responses in a date range (counts only video_response questions)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div>
              <label className="text-sm font-medium mb-2 block">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <Button onClick={runStep1} disabled={step1Loading} className="min-w-[150px]">
              {step1Loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
              Run Query
            </Button>
          </div>

          {orphanedResponses.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Found {orphanedResponses.length} orphaned responses ({orphanedResponses.reduce((s, r) => s + r.missing, 0)} missing questions)
                </span>
                <Button variant="outline" onClick={downloadStep1CSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </Button>
              </div>
              <div className="border rounded-lg max-h-[300px] overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Test</TableHead>
                      <TableHead className="text-center">Expected</TableHead>
                      <TableHead className="text-center">Saved</TableHead>
                      <TableHead className="text-center">Missing</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orphanedResponses.slice(0, 50).map((r) => (
                      <TableRow key={r.test_response_id}>
                        <TableCell>
                          <div className="font-medium">{r.candidate_name}</div>
                          <div className="text-xs text-muted-foreground">{r.candidate_email}</div>
                        </TableCell>
                        <TableCell>{r.test_title}</TableCell>
                        <TableCell className="text-center">{r.expected_questions}</TableCell>
                        <TableCell className="text-center">{r.saved_responses}</TableCell>
                        <TableCell className="text-center font-semibold text-amber-600">{r.missing}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2: Generate Question Timelines */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Step 2: Generate Question Timelines
          </CardTitle>
          <CardDescription>
            Upload CSV from Step 1 to generate timestamp windows for each missing question
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Upload Step 1 CSV</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleStep2FileUpload}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
            </div>
            <Button onClick={runStep2} disabled={step2Loading || !step2File} className="min-w-[150px]">
              {step2Loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Clock className="h-4 w-4 mr-2" />}
              Generate Timelines
            </Button>
          </div>

          {step2Progress && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Processing: {step2Progress.current} / {step2Progress.total}
                <Progress value={(step2Progress.current / step2Progress.total) * 100} className="mt-2" />
              </AlertDescription>
            </Alert>
          )}

          {questionTimelines.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Generated {questionTimelines.length} timeline entries ({questionTimelines.filter(t => t.is_missing).length} missing, {questionTimelines.filter(t => t.is_missing && t.has_monitoring_data).length} recoverable)
                </span>
                <Button variant="outline" onClick={downloadStep2CSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 3: Match Videos & Recover */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileVideo className="h-5 w-5" />
            Step 3: Match Videos & Recover Responses
          </CardTitle>
          <CardDescription>
            Upload CSV from Step 2 to match storage videos to questions and insert missing responses
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Upload Step 2 CSV</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleStep3FileUpload}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
            </div>
            <Button onClick={runStep3} disabled={step3Loading || !step3File} className="min-w-[150px]">
              {step3Loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileVideo className="h-4 w-4 mr-2" />}
              Recover Responses
            </Button>
          </div>

          {step3Progress && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Processing: {step3Progress.current} / {step3Progress.total}
                <Progress value={(step3Progress.current / step3Progress.total) * 100} className="mt-2" />
              </AlertDescription>
            </Alert>
          )}

          {recoveryResults.length > 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <Card className="border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-green-600">{recoveryResults.filter(r => r.status === 'recovered').length}</div>
                    <p className="text-xs text-muted-foreground">Recovered</p>
                  </CardContent>
                </Card>
                <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-amber-600">{recoveryResults.filter(r => r.status === 'not_found').length}</div>
                    <p className="text-xs text-muted-foreground">Not Found</p>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-blue-600">{recoveryResults.filter(r => r.status === 'already_exists').length}</div>
                    <p className="text-xs text-muted-foreground">Already Exists</p>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-red-600">{recoveryResults.filter(r => r.status === 'error').length}</div>
                    <p className="text-xs text-muted-foreground">Errors</p>
                  </CardContent>
                </Card>
              </div>
              <Button variant="outline" onClick={downloadStep3CSV}>
                <Download className="h-4 w-4 mr-2" />
                Download Results CSV
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 4: Trigger Evaluation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Step 4: Trigger Evaluation
          </CardTitle>
          <CardDescription>
            Upload CSV from Step 3 to trigger transcription and AI evaluation for recovered responses
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Upload Step 3 CSV</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleStep4FileUpload}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
            </div>
            <Button onClick={runStep4} disabled={step4Loading || !step4File} className="min-w-[150px]">
              {step4Loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
              Trigger Evaluation
            </Button>
          </div>

          {evaluationTriggered > 0 && (
            <Alert>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription>
                Triggered evaluation for {evaluationTriggered} question responses. They will be processed by the background evaluation pipeline.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Divider for Edge Cases */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">Edge Cases Recovery</span>
        </div>
      </div>

      {/* New Step: List Video Question Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileVideo className="h-5 w-5" />
            List Video Questions Status
          </CardTitle>
          <CardDescription>
            Upload Step 1 CSV to list all video questions with their URL status (MISSING / path)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Upload Step 1 CSV</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleStepListVideoFileUpload}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
            </div>
            <Button onClick={runStepListVideo} disabled={stepListVideoLoading || !stepListVideoFile} className="min-w-[150px]">
              {stepListVideoLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileVideo className="h-4 w-4 mr-2" />}
              List Video Questions
            </Button>
          </div>

          {stepListVideoProgress && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Processing: {stepListVideoProgress.current} / {stepListVideoProgress.total}
                <Progress value={(stepListVideoProgress.current / stepListVideoProgress.total) * 100} className="mt-2" />
              </AlertDescription>
            </Alert>
          )}

          {videoQuestionStatus.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Found {videoQuestionStatus.length} video questions ({videoQuestionStatus.filter(s => !s.is_missing).length} saved, {videoQuestionStatus.filter(s => s.is_missing).length} missing)
                </span>
                <Button variant="outline" onClick={downloadStepListVideoCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </Button>
              </div>
              <div className="border rounded-lg max-h-[200px] overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      <TableHead>Test Response ID</TableHead>
                      <TableHead className="text-center">Q#</TableHead>
                      <TableHead>Video URL</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {videoQuestionStatus.slice(0, 30).map((s, idx) => (
                      <TableRow key={`${s.test_response_id}-${s.question_id}-${idx}`}>
                        <TableCell className="font-mono text-xs">{s.test_response_id.slice(0, 8)}...</TableCell>
                        <TableCell className="text-center">{s.question_order}</TableCell>
                        <TableCell className="text-xs max-w-[200px] truncate">{s.video_url}</TableCell>
                        <TableCell className="text-center">
                          {s.is_missing ? (
                            <XCircle className="h-4 w-4 text-red-500 inline" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-green-500 inline" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 5: Analyze Storage Videos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Step 5: Analyze Storage Videos
          </CardTitle>
          <CardDescription>
            Upload Step 1 CSV to count videos in storage vs database for each test response
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Upload Step 1 CSV</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleStep5FileUpload}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
            </div>
            <Button onClick={runStep5} disabled={step5Loading || !step5File} className="min-w-[150px]">
              {step5Loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
              Analyze Storage
            </Button>
          </div>

          {step5Progress && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Processing: {step5Progress.current} / {step5Progress.total}
                <Progress value={(step5Progress.current / step5Progress.total) * 100} className="mt-2" />
              </AlertDescription>
            </Alert>
          )}

          {storageAnalysis.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Analyzed {storageAnalysis.length} responses ({storageAnalysis.filter(a => a.uninserted_video_count > 0).length} with uninserted videos)
                </span>
                <Button variant="outline" onClick={downloadStep5CSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </Button>
              </div>
              <div className="border rounded-lg max-h-[200px] overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      <TableHead>Candidate</TableHead>
                      <TableHead className="text-center">In Storage</TableHead>
                      <TableHead className="text-center">Inserted</TableHead>
                      <TableHead className="text-center">Uninserted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {storageAnalysis.filter(a => a.uninserted_video_count > 0).slice(0, 20).map((a) => (
                      <TableRow key={a.test_response_id}>
                        <TableCell>
                          <div className="font-medium">{a.candidate_name}</div>
                          <div className="text-xs text-muted-foreground">{a.candidate_email}</div>
                        </TableCell>
                        <TableCell className="text-center">{a.total_videos_in_storage}</TableCell>
                        <TableCell className="text-center">{a.total_videos_inserted}</TableCell>
                        <TableCell className="text-center font-semibold text-amber-600">{a.uninserted_video_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 5a: Video Inventory */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListVideo className="h-5 w-5" />
            Step 5a: Video Inventory
          </CardTitle>
          <CardDescription>
            Upload Step 5 CSV to get detailed list of all videos with insertion status
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Upload Step 5 CSV (filtered to uninserted_video_count {">"} 0)</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleStep5aFileUpload}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
            </div>
            <Button onClick={runStep5a} disabled={step5aLoading || !step5aFile} className="min-w-[150px]">
              {step5aLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ListVideo className="h-4 w-4 mr-2" />}
              Get Inventory
            </Button>
          </div>

          {step5aProgress && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Processing: {step5aProgress.current} / {step5aProgress.total}
                <Progress value={(step5aProgress.current / step5aProgress.total) * 100} className="mt-2" />
              </AlertDescription>
            </Alert>
          )}

          {videoInventory.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Found {videoInventory.length} videos ({videoInventory.filter(v => !v.is_inserted).length} uninserted)
                </span>
                <Button variant="outline" onClick={downloadStep5aCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 5b: Add Timelines to ALL Video Questions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Step 5b: Add Timelines to Video Questions
          </CardTitle>
          <CardDescription>
            Upload "List Video Questions" CSV to add monitoring timelines to ALL video questions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Upload "List Video Questions" CSV</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleStep5bFileUpload}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
            </div>
            <Button onClick={runStep5b} disabled={step5bLoading || !step5bFile} className="min-w-[150px]">
              {step5bLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Clock className="h-4 w-4 mr-2" />}
              Add Timelines
            </Button>
          </div>

          {step5bProgress && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Processing: {step5bProgress.current} / {step5bProgress.total}
                <Progress value={(step5bProgress.current / step5bProgress.total) * 100} className="mt-2" />
              </AlertDescription>
            </Alert>
          )}

          {missingTimelines.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Generated timelines for {missingTimelines.length} questions ({missingTimelines.filter(t => t.is_missing).length} missing, {missingTimelines.filter(t => t.is_missing && t.has_monitoring_data).length} recoverable)
                </span>
                <Button variant="outline" onClick={downloadStep5bCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 5c: Match Videos to Questions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Step 5c: Match Uninserted Videos to Missing Questions
          </CardTitle>
          <CardDescription>
            Upload both CSVs to generate proposed matches (NO INSERTS YET - review output before proceeding)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Upload Step 5a CSV (uninserted videos only)</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleStep5cVideoFileUpload}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Upload Step 5b CSV (missing timelines)</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleStep5cTimelineFileUpload}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
            </div>
          </div>
          <Button onClick={runStep5c} disabled={step5cLoading || !step5cVideoFile || !step5cTimelineFile} className="min-w-[150px]">
            {step5cLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
            Generate Matches
          </Button>

          {(proposedMatches.length > 0 || unmatchedQuestions.length > 0 || unmatchedVideos.length > 0) && (
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Review these results.</strong> Matches can proceed to Step 5d. Unmatched items are for diagnostics only.
                </AlertDescription>
              </Alert>

              {/* Matches summary */}
              {proposedMatches.length > 0 && (
                <>
                  <p className="text-sm font-medium">Proposed Matches ({proposedMatches.length})</p>
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-green-600">{proposedMatches.filter(m => m.confidence === 'high').length}</div>
                        <p className="text-xs text-muted-foreground">High Confidence</p>
                      </CardContent>
                    </Card>
                    <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-amber-600">{proposedMatches.filter(m => m.confidence === 'medium').length}</div>
                        <p className="text-xs text-muted-foreground">Medium Confidence</p>
                      </CardContent>
                    </Card>
                    <Card className="border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20">
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-red-600">{proposedMatches.filter(m => m.confidence === 'low').length}</div>
                        <p className="text-xs text-muted-foreground">Low Confidence</p>
                      </CardContent>
                    </Card>
                  </div>
                  <Button variant="outline" onClick={downloadStep5cMatchesCSV}>
                    <Download className="h-4 w-4 mr-2" />
                    Download Matches CSV ({proposedMatches.length})
                  </Button>
                </>
              )}

              {/* Unmatched summary */}
              {(unmatchedQuestions.length > 0 || unmatchedVideos.length > 0) && (
                <>
                  <p className="text-sm font-medium mt-4">Unmatched Items (Diagnostics)</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="border-slate-200 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-950/20">
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-slate-600">{unmatchedQuestions.length}</div>
                        <p className="text-xs text-muted-foreground">Unmatched Questions</p>
                        <p className="text-xs text-muted-foreground mt-1">No video found within ±80s of timeline</p>
                      </CardContent>
                    </Card>
                    <Card className="border-slate-200 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-950/20">
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-slate-600">{unmatchedVideos.length}</div>
                        <p className="text-xs text-muted-foreground">Unmatched Videos</p>
                        <p className="text-xs text-muted-foreground mt-1">Couldn't match to any question timeline</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="flex gap-2">
                    {unmatchedQuestions.length > 0 && (
                      <Button variant="outline" onClick={downloadStep5cUnmatchedQuestionsCSV}>
                        <Download className="h-4 w-4 mr-2" />
                        Download Unmatched Questions CSV ({unmatchedQuestions.length})
                      </Button>
                    )}
                    {unmatchedVideos.length > 0 && (
                      <Button variant="outline" onClick={downloadStep5cUnmatchedVideosCSV}>
                        <Download className="h-4 w-4 mr-2" />
                        Download Unmatched Videos CSV ({unmatchedVideos.length})
                      </Button>
                    )}
                  </div>
                </>
              )}

              {/* Response overlap summary */}
              {step5cSummary && (
                <div className="text-xs text-muted-foreground border-t pt-4 mt-4">
                  <p><strong>Response Analysis:</strong> {step5cSummary.responses_with_both} responses with both videos & questions, 
                    {step5cSummary.responses_with_only_videos} with only videos, 
                    {step5cSummary.responses_with_only_questions} with only questions</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 5d: Execute Inserts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PenTool className="h-5 w-5" />
            Step 5d: Execute Database Inserts
          </CardTitle>
          <CardDescription>
            Upload reviewed CSV from Step 5c to insert matched videos into question_responses
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription>
              <strong>Warning:</strong> This step will INSERT records into the database. Make sure you have reviewed the CSV from Step 5c.
            </AlertDescription>
          </Alert>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Upload Reviewed Step 5c CSV</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleStep5dFileUpload}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
            </div>
            <Button onClick={runStep5d} disabled={step5dLoading || !step5dFile} variant="destructive" className="min-w-[150px]">
              {step5dLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PenTool className="h-4 w-4 mr-2" />}
              Execute Inserts
            </Button>
          </div>

          {insertResults.length > 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-green-600">{insertResults.filter(r => r.status === 'inserted').length}</div>
                    <p className="text-xs text-muted-foreground">Inserted</p>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-blue-600">{insertResults.filter(r => r.status === 'already_exists').length}</div>
                    <p className="text-xs text-muted-foreground">Already Exists</p>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-red-600">{insertResults.filter(r => r.status === 'error').length}</div>
                    <p className="text-xs text-muted-foreground">Errors</p>
                  </CardContent>
                </Card>
              </div>
              <Button variant="outline" onClick={downloadStep5dCSV}>
                <Download className="h-4 w-4 mr-2" />
                Download Insert Results CSV
              </Button>
              <Alert>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  After successful inserts, use <strong>Step 4</strong> with the Step 5d results CSV to trigger evaluation for the newly inserted responses.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
